import React, { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import ExpoConstants from 'expo-constants';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import Translations from 'src/core/utils/Translations';
import type { ApiLocationAddress } from 'src/core/models/api/shared/ApiLocationAddress';
import type { ContentBlockLocationProps } from 'src/presentation/components/organisms/contentblocks/types/ContentBlockTypes';

/**
 * Web sibling of ContentBlockLocation. Native renders react-native-maps, which calls
 * codegenNativeComponent while being imported and takes down the React tree on web. The map
 * here comes from the Google Maps JavaScript API drawn into a DOM node — the same provider
 * react-native-maps uses on Android, so both platforms show the same imagery.
 *
 * Without a key, or when the script cannot load, the block keeps the address panel that links
 * out to Google Maps. A location block never becomes an empty white box.
 */

const TAG = '[CONTENTBLOCK-LOCATION] ->';

const MAP_HEIGHT = 210;
// Roughly the 1 km across that the native block asks regionFrom() for at this height.
const MAP_ZOOM = 15;

// MAP-02: per-environment key from expo.extra (see app.config.ts), never a literal in the
// bundle. Read the same way LinkingConfiguration reads its origins.
const GOOGLE_MAPS_API_KEY = (ExpoConstants.expoConfig?.extra?.googleMapsApiKey as string | undefined) ?? '';

/* --- Google Maps JS API ---------------------------------------------------
 * Only the surface this component touches: @types/google.maps is not a dependency here and
 * the map is loaded as a plain script, so the shapes are declared locally. */

interface MapsLatLngLiteral {
	lat: number;
	lng: number;
}

interface MapsMapOptions {
	center: MapsLatLngLiteral;
	zoom: number;
	gestureHandling?: 'cooperative' | 'greedy' | 'none' | 'auto';
	mapTypeControl?: boolean;
	streetViewControl?: boolean;
	fullscreenControl?: boolean;
}

// Opaque handle: the component creates a map and only hands it back to the API on teardown.
type MapsMap = object;

interface MapsMarkerOptions {
	position: MapsLatLngLiteral;
	map: MapsMap;
	title?: string;
}

interface MapsMarker {
	setMap(map: MapsMap | null): void;
}

interface MapsApi {
	Map: new (container: HTMLElement, options: MapsMapOptions) => MapsMap;
	// google.maps.Marker is deprecated in favour of AdvancedMarkerElement, which needs a Map ID
	// per environment that this project does not have. Marker keeps working and stays the
	// smaller surface; migrating is a follow-up for when a Map ID is provisioned.
	Marker: new (options: MapsMarkerOptions) => MapsMarker;
	event: { clearInstanceListeners(instance: object): void };
}

const MAPS_SCRIPT_ID = 'google-maps-js-api';
// `loading=async` requires a callback name, so the script reports readiness through a global.
const MAPS_CALLBACK_NAME = '__topicusEventsGoogleMapsReady';

interface MapsWindow extends Window {
	google?: { maps?: MapsApi };
	[MAPS_CALLBACK_NAME]?: () => void;
}

/**
 * One script tag per page, shared by every location block on it: the promise is cached at
 * module scope, so a second block reuses the first block's load instead of appending another
 * tag. A rejection stays cached on purpose — a key the API refuses fails the same way on every
 * retry, and retrying would add a script tag per mount.
 */
let mapsApiPromise: Promise<MapsApi> | null = null;

function getMapsWindow(): MapsWindow | undefined {
	return typeof window === 'undefined' ? undefined : window;
}

function loadMapsApi(apiKey: string): Promise<MapsApi> {
	if (mapsApiPromise) {
		return mapsApiPromise;
	}

	mapsApiPromise = new Promise<MapsApi>((resolve, reject) => {
		const mapsWindow = getMapsWindow();
		if (!mapsWindow || typeof document === 'undefined') {
			reject(new Error('no DOM to load the Google Maps script into'));
			return;
		}

		// Another bundle (or a hot reload) may have loaded the API already.
		const loaded = mapsWindow.google?.maps;
		if (loaded) {
			resolve(loaded);
			return;
		}

		mapsWindow[MAPS_CALLBACK_NAME] = () => {
			delete mapsWindow[MAPS_CALLBACK_NAME];
			const api = mapsWindow.google?.maps;
			if (api) {
				resolve(api);
			} else {
				reject(new Error('the Google Maps script ran without exposing google.maps'));
			}
		};

		const script = document.createElement('script');
		script.id = MAPS_SCRIPT_ID;
		script.async = true;
		script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&callback=${MAPS_CALLBACK_NAME}`;
		script.onerror = () => {
			delete mapsWindow[MAPS_CALLBACK_NAME];
			reject(new Error('the Google Maps script failed to load'));
		};

		document.head.appendChild(script);
	});

	return mapsApiPromise;
}

// Plain CSS: the map container is a DOM node, so StyleSheet.create ids mean nothing to it.
const mapStyle = { width: '100%', height: '100%' };

export default function ContentBlockLocation({ data, first }: ContentBlockLocationProps) {
	const { openUrlExternally } = useServicesStore();
	const addressData = JSON.parse(data.data.address) as ApiLocationAddress;
	const { lat, lng } = addressData.latlng;
	const address = addressData.value;

	const mapContainer = useRef<HTMLDivElement | null>(null);
	const [mapsApi, setMapsApi] = useState<MapsApi | null>(null);
	const [mapReady, setMapReady] = useState(false);
	// No key means no map to wait for: go straight to the address panel.
	const [failed, setFailed] = useState(!GOOGLE_MAPS_API_KEY);

	useEffect(() => {
		if (!GOOGLE_MAPS_API_KEY) {
			return;
		}

		let cancelled = false;
		loadMapsApi(GOOGLE_MAPS_API_KEY)
			.then((api) => {
				if (!cancelled) {
					setMapsApi(api);
				}
			})
			.catch((error: Error) => {
				console.warn(`${TAG} falling back to the address panel:`, error.message);
				if (!cancelled) {
					setFailed(true);
				}
			});

		// The script tag itself is page-wide and stays: the Maps API cannot be unloaded cleanly,
		// and the next block to mount reuses it. Only this component's interest in the load ends.
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		const container = mapContainer.current;
		if (!mapsApi || !container) {
			return;
		}

		const position = { lat, lng };
		let map: MapsMap | null = null;
		let marker: MapsMarker | null = null;

		try {
			map = new mapsApi.Map(container, {
				center: position,
				zoom: MAP_ZOOM,
				// The block sits in a scrolling page: a plain scroll passes over the map, and
				// ctrl/cmd + scroll (two fingers on touch) zooms it.
				gestureHandling: 'cooperative',
				mapTypeControl: false,
				streetViewControl: false,
				fullscreenControl: false,
			});
			marker = new mapsApi.Marker({ position, map, title: address });
			setMapReady(true);
		} catch (error) {
			console.warn(`${TAG} could not create the map:`, error);
			setFailed(true);
		}

		return () => {
			if (marker) {
				marker.setMap(null);
				mapsApi.event.clearInstanceListeners(marker);
			}
			if (map) {
				mapsApi.event.clearInstanceListeners(map);
			}
			// The API fills the container with its own DOM; drop it so a remount starts clean.
			container.replaceChildren();
			setMapReady(false);
		};
		// The parsed address is a new object every render, so depend on its values instead.
	}, [mapsApi, lat, lng, address]);

	function onButtonPress(latitude: number, longitude: number) {
		// openUrlExternally hands the URL to Linking, which targets _blank on react-native-web, so
		// the map opens in a new tab and the app keeps its state.
		openUrlExternally({ url: `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}` }).catch(
			() => undefined,
		);
	}

	return (
		<View style={[styles.container, first ? styles.first : null]}>
			<View style={styles.panel}>
				{failed ? null : <div aria-label={Translations.labelMap} ref={mapContainer} style={mapStyle} />}
				{mapReady ? null : (
					<View style={styles.placeholder}>
						<Ionicons name="location" size={40} style={styles.pin} />
						{address ? <Text style={styles.address}>{address}</Text> : null}
					</View>
				)}
			</View>
			<TouchableOpacity activeOpacity={0.6} style={styles.buttonWrapper} onPress={() => onButtonPress(lat, lng)}>
				<View style={styles.button}>
					<Text style={styles.text}>{Translations.buttonNavigateLocationUpper}</Text>
				</View>
			</TouchableOpacity>
		</View>
	);
}

const styles = StyleSheet.create({
	container: {
		marginTop: Dimens.margin10,
	},
	first: {
		marginTop: Dimens.margin0,
	},
	panel: {
		height: MAP_HEIGHT,
		backgroundColor: Colors.greyBgAlt,
		overflow: 'hidden',
	},
	// Covers the map container until the map is drawn, so a slow load shows the address
	// instead of a blank frame.
	placeholder: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: Colors.greyBgAlt,
		alignItems: 'center',
		justifyContent: 'center',
		paddingHorizontal: Dimens.padding16,
	},
	pin: {
		color: Colors.greyDark,
		marginBottom: Dimens.margin10,
	},
	address: {
		fontFamily: Fonts.montserratMedium,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
		textAlign: 'center',
	},
	buttonWrapper: {
		width: '100%',
		alignItems: 'center',
		justifyContent: 'center',
	},
	text: {
		fontFamily: Fonts.montserratMedium,
		fontSize: Dimens.fontSize14,
		color: Colors.white,
		paddingLeft: 22,
		paddingRight: 22,
		paddingTop: 17,
		paddingBottom: 17,
	},
	button: {
		backgroundColor: Colors.secondary,
		marginTop: Dimens.margin20,
		marginBottom: Dimens.margin20,
		minWidth: 130,
		alignItems: 'center',
		justifyContent: 'center',
	},
});
